import React, { useState, useEffect } from 'react';
import { Platform } from 'react-native';
import { Box, Text, Pressable, useTheme, Avatar, HStack, VStack, Circle, useBreakpointValue, View } from 'native-base';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { addInlineStyleToMentionBlot, displayTime, getCategoryNames, getSourceHTML, isArrayValid } from '../utils/helpers';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { DEVICE_WIDTH, TYPE_MENTION, TYPE_EDITED, TYPE_LIKE, TYPE_REPLY, TYPE_POST, AVATAR_SIZE, NOTIFICATION_TYPE_ICONS } from '../Constants';
import { useAuthState } from '../../context/auth';
import { overviewAPI, useOverviewDispatch, useOverviewState } from '../../context/overview';
import { useCategoriesState } from '../../context/categories';
import { useEnvState } from '../../context/env';
import RenderHtml from 'react-native-render-html';
import { topicsAPI } from '../../context/topics';
import { useDomParser } from '../hooks/useDomParser';
import ContextMenu from './ContextMenu';
const Activity = _ref => {
  let {
    activity,
    navigation,
    updateNotifications,
    setIsPopoverOpen,
    selected,
    loadContents
  } = _ref;
  const theme = useTheme(),
    {
      colors
    } = theme;
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const [notificationTypeText, setNotificationTypeText] = useState('');
  const authState = useAuthState();
  const envState = useEnvState();
  const categoriesState = useCategoriesState();
  const {
    forumCategories: categories,
    rootProductCategory
  } = categoriesState;
  const {
    getParser,
    normalizeImages,
    displayAtMentions
  } = useDomParser();
  const {
    id,
    read,
    notification_type,
    created_at,
    topic_id,
    fancy_title,
    data
  } = activity;
  const {
    original_post_id,
    display_username,
    notifying_user_name,
    avatar_template,
    category_id,
    excerpt
  } = data;
  const avatarTemplate = avatar_template ? avatar_template.replace(/\{size\}/, AVATAR_SIZE) : null;
  const categoryIds = categories.map(category => category.id);
  const categoryExists = category_id && categoryIds.includes(category_id);
  const categoriesInfo = categoryExists ? getCategoryNames(category_id, rootProductCategory, categories) : '';
  const categoryName = isArrayValid(categoriesInfo) ? categoriesInfo[categoriesInfo.length - 1] : '';
  const overviewState = useOverviewState();
  const overviewDispatch = useOverviewDispatch();
  useEffect(() => {
    displayNotificationTypeText();
  }, [notification_type]);
  const excerptWithPTag = `<p>${excerpt}</p>`; // excerpt initially has no html tags such as <p>
  let sourceHTML = getSourceHTML(excerptWithPTag, colors);

  // parse/normalize markup
  const parser = getParser();
  if (parser) {
    sourceHTML = normalizeImages(parser, sourceHTML, DEVICE_WIDTH);
    sourceHTML = displayAtMentions(parser, sourceHTML);
    sourceHTML = addInlineStyleToMentionBlot(parser, sourceHTML, colors);
  }
  const setIconByType = () => {
    const icon = NOTIFICATION_TYPE_ICONS.find(icon => icon.type === notification_type);
    if (icon) {
      return icon.icon;
    }
  };
  const handleOnPress = async () => {
    if (!activity.read) {
      // update to read if !read
      const response = await overviewAPI.setActivityToRead(envState.base_url, authState.api_key, id);
      if (!response) {
        throw new Error("Couldn't mark notification as read. Please try again");
      } else {
        // set to read in UI
        updateNotifications && updateNotifications(id);

        // update number of unread activities
        overviewDispatch({
          type: 'SET_UNREAD_ACTIVITY_COUNT',
          payload: overviewState.unreadActivityCount - 1
        });
        loadContents ? loadContents() : console.log(''); // state persists when pressing Go back button
        setIsPopoverOpen && setIsPopoverOpen(false);
      }
    }
    const originalReply = await topicsAPI.getReply(envState.base_url, authState.api_key, original_post_id);
    if (originalReply) {
      navigation.navigate('SingleTopic', {
        topicId: topic_id,
        normalizeUrl: true,
        replyId: original_post_id,
        reply: originalReply
      });
    }
  };
  const displayNotificationTypeText = () => {
    switch (notification_type) {
      case TYPE_MENTION:
        setNotificationTypeText('mentioned you');
        break;
      case TYPE_EDITED:
        setNotificationTypeText('edited post');
        break;
      case TYPE_LIKE:
        setNotificationTypeText('liked your post');
        break;
      case TYPE_REPLY:
        setNotificationTypeText('wrote a reply');
        break;
      case TYPE_POST:
        setNotificationTypeText('wrote a post');
        break;
      default:
        setNotificationTypeText('');
    }
  };
  const notificationIcon = setIconByType();
  const descriptiveIcon = /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: notificationIcon,
    color: colors.activity.text
  });
  const descriptiveText = /*#__PURE__*/React.createElement(Text, {
    fontSize: "xs",
    color: colors.activity.notificationTypeText
  }, notificationTypeText);
  const description = useBreakpointValue({
    base: descriptiveIcon,
    '3xs': descriptiveIcon,
    '2xs': descriptiveText,
    sm: descriptiveText // nothing popped up from "sm" and above until I added this
  });

  return /*#__PURE__*/React.createElement(Pressable, {
    onPress: handleOnPress,
    borderRadius: 5,
    w: "100%"
  }, /*#__PURE__*/React.createElement(Box, {
    p: "4",
    bg: read ? colors.activity.read : colors.activity.unread,
    mx: "2",
    my: "1",
    borderRadius: 5
  }, /*#__PURE__*/React.createElement(VStack, {
    space: 3
  }, /*#__PURE__*/React.createElement(HStack, {
    space: 2,
    alignItems: 'center',
    w: "100%"
  }, avatar_template && /*#__PURE__*/React.createElement(Avatar, {
    source: {
      uri: `${envState.base_url}${avatarTemplate}`
    },
    size: 'sm',
    mx: 2,
    mt: 2
  }), /*#__PURE__*/React.createElement(VStack, null, /*#__PURE__*/React.createElement(HStack, {
    alignItems: "center",
    space: 1
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: 'sm',
    fontWeight: 'semibold'
  }, notifying_user_name ? notifying_user_name + '.' : display_username), /*#__PURE__*/React.createElement(View, null, description)), /*#__PURE__*/React.createElement(HStack, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 'xs',
    color: colors.activity.timestamp
  }, displayTime(created_at), " ago"), categoryExists ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 'xs',
    color: colors.activity.timestamp
  }, ' ', "in", ' '), /*#__PURE__*/React.createElement(Text, {
    fontSize: 'xs',
    color: colors.activity.link
  }, categoryName)) : '')), /*#__PURE__*/React.createElement(HStack, {
    alignItems: "center",
    space: 4,
    position: "absolute",
    top: "0",
    right: "0"
  }, /*#__PURE__*/React.createElement(ContextMenu, {
    id: id,
    read: read,
    selected: selected,
    updateNotifications: updateNotifications,
    isContextMenuOpen: isContextMenuOpen,
    setIsContextMenuOpen: setIsContextMenuOpen
  }), !read && /*#__PURE__*/React.createElement(Circle, {
    size: '12px',
    bg: colors.activity.link
  }))), excerpt ? /*#__PURE__*/React.createElement(RenderHtml, {
    defaultTextProps: {
      selectable: true
    },
    contentWidth: Platform.OS === 'web' ? null : DEVICE_WIDTH,
    source: {
      html: sourceHTML
    }
  }) : /*#__PURE__*/React.createElement(Text, {
    fontSize: 'sm',
    color: colors.activity.link
  }, fancy_title), /*#__PURE__*/React.createElement(HStack, {
    alignItems: 'center',
    space: 1
  }, /*#__PURE__*/React.createElement(Text, {
    color: colors.activity.link
  }, "Go to post"), /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faChevronRight,
    color: colors.activity.link
  })))));
};
export default Activity;
//# sourceMappingURL=Activity.js.map